/**
 * useTimezone — React hook for user-preference-aware timezone formatting
 *
 * Reads the authenticated user's `preferences.timezone` from the Redux store
 * and returns timezone info + a pre-bound date formatter that respects the
 * user's chosen timezone.
 *
 * Uses native `Intl.DateTimeFormat` — no external timezone library needed.
 *
 * Usage:
 *   const { timezone, formatInUserTimezone } = useTimezone();
 *   return <span>{formatInUserTimezone(transaction.date)}</span>;
 *
 * [T114-F3-j] Timezone standardization
 */
import { useCallback, useMemo } from 'react';
import { useAppSelector } from '@/features/hooks';

export interface UseTimezoneReturn {
  /** The IANA timezone identifier from the user's preferences, e.g. "Asia/Bangkok" */
  timezone: string;
  /** The UTC offset string for the user's timezone, e.g. "UTC+07:00" */
  utcOffset: string;
  /** The abbreviated timezone name, e.g. "ICT", "EST" */
  abbreviation: string;
  /**
   * Format a date/time string or Date in the user's preferred timezone.
   * @param date — ISO string or Date object
   * @param formatStr — Optional format hint: 'short' | 'medium' | 'long' | 'date-only' | 'time-only'
   *   Defaults to 'medium' which renders like "Apr 4, 2026, 2:30 PM"
   */
  formatInUserTimezone: (date: string | Date, formatStr?: FormatStyle) => string;
  /**
   * Standardized display formatter [T125-F1-p].
   * - `short`:    "Apr 12" (current year) or "Apr 12, 2025" (other year)
   * - `medium`:   "Apr 12, 2026"
   * - `long`:     "Saturday, April 12, 2026 at 3:45 PM WIB"
   * - `relative`: "2 hours ago", "Yesterday", "3 days ago"
   */
  formatDate: (date: string | Date, style: DisplayStyle) => string;
}

type FormatStyle = 'short' | 'medium' | 'long' | 'date-only' | 'date-weekday' | 'time-only';

/** Standardized display format styles [T125-F1-p] */
export type DisplayStyle = 'short' | 'medium' | 'long' | 'relative';

function getUtcOffset(timezone: string): string {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    });
    const parts = formatter.formatToParts(now);
    const tzPart = parts.find((p) => p.type === 'timeZoneName');
    // tzPart.value is like "GMT+7" or "GMT-5:30" or "GMT"
    const raw = tzPart?.value ?? 'UTC';
    // Normalize "GMT" → "UTC", "GMT+7" → "UTC+07:00"
    return raw.replace('GMT', 'UTC').replace(/([+-])(\d)(?::(\d{2}))?$/, (_m, sign, h, min) => {
      return `${sign}${h.padStart(2, '0')}:${min || '00'}`;
    });
  } catch {
    return 'UTC';
  }
}

function getAbbreviation(timezone: string): string {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    });
    const parts = formatter.formatToParts(now);
    const tzPart = parts.find((p) => p.type === 'timeZoneName');
    return tzPart?.value ?? timezone;
  } catch {
    return timezone;
  }
}

function formatDate(date: string | Date, timezone: string, style: FormatStyle): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  const options: Intl.DateTimeFormatOptions = { timeZone: timezone };

  switch (style) {
    case 'short':
      options.month = 'short';
      options.day = 'numeric';
      break;
    case 'medium':
      options.year = 'numeric';
      options.month = 'short';
      options.day = 'numeric';
      options.hour = 'numeric';
      options.minute = '2-digit';
      options.hour12 = true;
      break;
    case 'long':
      options.year = 'numeric';
      options.month = 'long';
      options.day = 'numeric';
      options.hour = 'numeric';
      options.minute = '2-digit';
      options.second = '2-digit';
      options.hour12 = true;
      options.timeZoneName = 'short';
      break;
    case 'date-only':
      options.year = 'numeric';
      options.month = 'short';
      options.day = 'numeric';
      break;
    case 'date-weekday':
      options.weekday = 'short';
      options.year = 'numeric';
      options.month = 'short';
      options.day = 'numeric';
      break;
    case 'time-only':
      options.hour = 'numeric';
      options.minute = '2-digit';
      options.hour12 = true;
      break;
  }

  return new Intl.DateTimeFormat('en-US', options).format(dateObj);
}

// ── Timezone-aware date boundary utilities [T125-F1-m] ──────────────────────

/**
 * Compute the UTC offset in milliseconds for a given IANA timezone.
 * Positive = ahead of UTC (e.g. Asia/Jakarta = +25200000).
 */
function getTimezoneOffsetMs(timezone: string): number {
  const now = new Date();
  const utcStr = now.toLocaleString('en-US', { timeZone: 'UTC' });
  const tzStr = now.toLocaleString('en-US', { timeZone: timezone });
  return new Date(tzStr).getTime() - new Date(utcStr).getTime();
}

/**
 * Get the start of today (midnight) in the given timezone, returned as a UTC Date.
 * Example: Asia/Jakarta (UTC+7) on April 12 → April 11 17:00:00 UTC.
 */
export function getStartOfDayInTimezone(timezone: string): Date {
  const now = new Date();
  // Get today's date string in the user's timezone (YYYY-MM-DD via en-CA locale)
  const dateStr = now.toLocaleDateString('en-CA', { timeZone: timezone });
  // Midnight UTC on that calendar date
  const utcMidnight = new Date(dateStr + 'T00:00:00Z');
  // Shift back by the timezone offset to get midnight-in-timezone as a UTC timestamp
  const offsetMs = getTimezoneOffsetMs(timezone);
  return new Date(utcMidnight.getTime() - offsetMs);
}

/**
 * Get the end of today (23:59:59.999) in the given timezone, returned as a UTC Date.
 */
export function getEndOfDayInTimezone(timezone: string): Date {
  const start = getStartOfDayInTimezone(timezone);
  return new Date(start.getTime() + 24 * 60 * 60 * 1000 - 1);
}

// ── Standardized display formatter [T125-F1-p] ──────────────────────────────

/**
 * Format a date with standardized display styles, timezone-aware.
 * - `short`:    "Apr 12" (current year) or "Apr 12, 2025" (other year)
 * - `medium`:   "Apr 12, 2026"
 * - `long`:     "Saturday, April 12, 2026 at 3:45 PM WIB"
 * - `relative`: "2 hours ago", "Yesterday", "3 days ago"
 */
export function formatDateDisplay(
  date: string | Date,
  timezone: string,
  style: DisplayStyle,
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (style === 'relative') {
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);

    if (diffSec < 60) return 'Just now';
    if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
    if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
    if (diffDay === 1) return 'Yesterday';
    if (diffDay < 30) return `${diffDay} days ago`;
    if (diffDay < 365) return `${Math.floor(diffDay / 30)} month${Math.floor(diffDay / 30) === 1 ? '' : 's'} ago`;
    return `${Math.floor(diffDay / 365)} year${Math.floor(diffDay / 365) === 1 ? '' : 's'} ago`;
  }

  const options: Intl.DateTimeFormatOptions = { timeZone: timezone };

  switch (style) {
    case 'short': {
      // "Apr 12" for current year, "Apr 12, 2025" for other years
      const nowYear = new Date().toLocaleDateString('en-CA', { timeZone: timezone }).slice(0, 4);
      const dateYear = dateObj.toLocaleDateString('en-CA', { timeZone: timezone }).slice(0, 4);
      options.month = 'short';
      options.day = 'numeric';
      if (nowYear !== dateYear) options.year = 'numeric';
      break;
    }
    case 'medium':
      options.year = 'numeric';
      options.month = 'short';
      options.day = 'numeric';
      break;
    case 'long':
      options.weekday = 'long';
      options.year = 'numeric';
      options.month = 'long';
      options.day = 'numeric';
      options.hour = 'numeric';
      options.minute = '2-digit';
      options.hour12 = true;
      options.timeZoneName = 'short';
      break;
  }

  return new Intl.DateTimeFormat('en-US', options).format(dateObj);
}

export function useTimezone(): UseTimezoneReturn {
  const timezone = useAppSelector(
    (state) => state.auth.user?.preferences?.timezone ?? 'UTC'
  );

  const utcOffset = useMemo(() => getUtcOffset(timezone), [timezone]);
  const abbreviation = useMemo(() => getAbbreviation(timezone), [timezone]);

  const formatInUserTimezone = useCallback(
    (date: string | Date, formatStr: FormatStyle = 'date-only') =>
      formatDate(date, timezone, formatStr),
    [timezone]
  );

  const formatDateStyled = useCallback(
    (date: string | Date, style: DisplayStyle) =>
      formatDateDisplay(date, timezone, style),
    [timezone]
  );

  return { timezone, utcOffset, abbreviation, formatInUserTimezone, formatDate: formatDateStyled };
}
